import { InnerBox } from "@/components/elements/InnerBox";
import { DATE_FORMAT } from "@/constants/datetime";
import { CaseMatchStatusTag } from "@/features/talent/components/talent-common/CaseMatchStatusTag";
import { TCaseMatches, TTalentForm } from "@/features/talent/types/talentForm";
import { AppRoutes } from "@/routes/config";
import { divider, black, secondary } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { CaseMatchStatusOptions } from "@/types/enum";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Box, Typography, Link, styled } from "@mui/material";
import dayjs from "dayjs";

type TTalentProjectParticipationStatusProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentProjectParticipationStatus: FCC<TTalentProjectParticipationStatusProps> = ({ defaultValues }) => {
  const handleCaseClick = (caseMatch: TCaseMatches) => {
    const caseId = caseMatch.case?.id ?? caseMatch.caseId;
    const jobId = caseMatch.case?.jobId;

    if (jobId && caseId) {
      const caseEditRoute = getDynamicRoute(AppRoutes.caseEdit, {
        jobId: String(jobId),
        caseId: String(caseId),
      });

      window.open(caseEditRoute, "_blank");
    }
  };

  return (
    <InnerBox p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="head20Bold" color={black[30]}>
            案件参加状況
          </Typography>
        </Box>
      </Stack>

      <ScrollableBox marginTop={defaultValues?.caseMatches?.length ? "24px" : "0px"}>
        {defaultValues?.caseMatches?.length
          ? defaultValues.caseMatches.map((item, index) => (
              <Stack
                key={index}
                mt={index > 0 ? 2 : 0}
                p={2}
                border={`1px solid ${black[99]}`}
                borderRadius={rounded.sm}
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                {/* left */}
                <Stack flex={0.7}>
                  {item?.case?.job?.company?.companyName && (
                    <Typography variant="body14" color={black[30]} sx={{ mb: 0.5 }}>
                      {item?.case?.job?.company?.companyName}
                    </Typography>
                  )}

                  {item?.case?.name && (
                    <Typography variant="body14" color={black[30]} sx={{ mb: 0.5 }}>
                      {item?.case?.name}
                    </Typography>
                  )}

                  {item?.proposalDate && (
                    <Typography variant="cap12" color={black[30]} sx={{ mb: 0.5 }}>
                      {dayjs(item.proposalDate).format(DATE_FORMAT)}
                    </Typography>
                  )}
                </Stack>

                {/* right */}
                <Stack spacing={0.5} flex={0.3} alignItems="flex-start">
                  <CaseMatchStatusTag
                    status={item?.status}
                    title={findLabelByValue(CaseMatchStatusOptions, item?.status)}
                    variant="bold"
                    sx={{
                      padding: "4px 16px",
                    }}
                  />
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => handleCaseClick(item)}
                    sx={{
                      mt: "7px",
                      textDecoration: "none",
                      color: secondary[80],
                      cursor: "pointer",
                      ...typography.cap12,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    詳細→
                  </Link>
                </Stack>
              </Stack>
            ))
          : null}
      </ScrollableBox>
    </InnerBox>
  );
};

const ScrollableBox = styled(Box)<{ maxHeight?: string | number; marginTop?: string }>(({ maxHeight = "609px", marginTop = "24px" }) => ({
  maxHeight,
  marginTop,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: black[99],
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: black[90],
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: black[80],
    },
  },
}));
